import React from 'react';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import Backdrop from '@material-ui/core/Backdrop';

// @design-system
import NothingFound from '../../../shared/components/NothingFound/NothingFound';
import Toast from '../../../design-system/components/Toast/Toast';
import Table from '../../../design-system/components/Table/Table';
import Select from '../../../design-system/components/Form/Select/Select';

// @material-core
import {
    Box,
    Button,
    Card,
    CardContent,
    Modal,
    Typography,
    Checkbox,
    Icon,
    Grid,
    Switch,
    FormControlLabel,
    makeStyles
} from '@material-ui/core';
import { green } from '@material-ui/core/colors';
import queryString from 'query-string';
import { isNil } from 'ramda';

// @local
import { baseRequests } from '../ColumnsConfig';
import LoaderContent from '../../../design-system/components/Loader/LoaderContent';
import { useStyles } from '../../../assets/styles/jss/pages/microcredentials/useStyles';

// SDK
import { ThinkificService } from '@sdk-point/talisis';

// Estilos para el Select con color morado y fondo oscuro
const useSelectStyles = makeStyles((theme) => ({
    selectRoot: {
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#1C1F24',
            borderRadius: '8px',
            '& fieldset': {
                borderColor: '#9F6FFB',
                borderWidth: '1px',
            },
            '&:hover fieldset': {
                borderColor: '#9F6FFB',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#9F6FFB',
                borderWidth: '1px',
            },
            '& .MuiSelect-root': {
                color: '#FFFFFF',
            },
            '& .MuiSelect-icon': {
                color: '#A0A9BA',
            },
        },
    },
}));

const ThinkificServiceSDK = new ThinkificService(
    process.env.REACT_APP_ENV_V2,
    process.env.REACT_APP_X_API_KEY_V2
);

const MgmtMicrocredentialsTablePage = () => {
    const classes = useStyles();
    const selectClasses = useSelectStyles();
    const location = useLocation();

    const [toast, setToast] = React.useState({ toastProps: { message: '', open: false } });
    const [columns, setColumns] = React.useState([]);
    const [state, setState] = React.useState({ data: [], dataSource: [] });
    const [loading, setLoading] = React.useState(false);
    const [loadingMicroId, setLoadingMicroId] = React.useState(false);
    const [selectedFilters, setSelectedFilters] = React.useState({});
    const [catalogues, setCatalogues] = React.useState({ microId: [] });
    const [selectedRows, setSelectedRows] = React.useState([]);
    const [selectAll, setSelectAll] = React.useState(false);
    const [emitting, setEmitting] = React.useState(false);
    const [progress, setProgress] = React.useState(0);
    const [isRefreshEnabled, setIsRefreshEnabled] = React.useState(false);
    const [refreshInterval, setRefreshInterval] = React.useState(''); 
    const refreshIntervalRef = React.useRef(null);

    const messageTable = {
        caption: 'No se encontraron resultados.',
        subtitle: 'Intenta con otra microcredencial'
    };

    const getRowId = (row, idx) => {
        const id = row?.id ?? row?.user_id ?? row?.userId;
        return id !== undefined ? `${id}-${idx}` : idx;
    };

    // Función helper para actualizar los parámetros de refresh en la URL
    const updateRefreshParamsInUrl = (interval, enabled) => {
        const currentPath = window.location.pathname;
        const params = new URLSearchParams(window.location.search);
        
        if (interval && enabled) {
            params.set('refreshInterval', interval);
            params.set('refreshEnabled', enabled.toString());
        } else {
            params.delete('refreshInterval');
            params.delete('refreshEnabled');
        }
        
        const queryString = params.toString();
        const newUrl = queryString ? `${currentPath}?${queryString}` : currentPath;
        window.history.replaceState({}, '', newUrl);
    };

    React.useEffect(() => {
        const params = queryString.parse(location.search);
        setSelectedFilters(params);
        setLoadingMicroId(true);
        getMicrocredentials();

        // Restaurar el refreshInterval y isRefreshEnabled desde los parámetros de la URL
        if (params.refreshInterval) {
            setRefreshInterval(params.refreshInterval);
        }
        if (params.refreshEnabled === 'true') {
            setIsRefreshEnabled(true);
        }

        if (!isNil(params.microId)) {
            getAllRequests(params.microId);
        }
    }, []);

    // Efecto para manejar el refresh automático
    React.useEffect(() => {
        if (isRefreshEnabled && refreshInterval && parseInt(refreshInterval, 10) > 0) {
            refreshIntervalRef.current = setInterval(() => {
                // Recargar solo los datos sin recargar toda la página
                if (selectedFilters.microId) {
                    getAllRequests(selectedFilters.microId);
                }
            }, parseInt(refreshInterval, 10) * 1000);
        } else {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
            }
        }

        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
            }
        };
    }, [isRefreshEnabled, refreshInterval, selectedFilters.microId]);

    // ---------------------- Microcredenciales ----------------------
    const getMicrocredentials = async () => {
        try {
            const { data } = await ThinkificServiceSDK.getCatalogs({ CatalogType: "microcredential" });
            const microId = data.map(item => ({
                id: item.id,
                label: item.name,
                value: item.id,
            }));
            setCatalogues({ microId });
        } catch (err) {
            setToast({
                toastProps: {
                    severity: "error",
                    open: true,
                    message: err?.message || "Ops! Ha ocurrido un error"
                }
            });
        }
        setLoadingMicroId(false);
    };

    // ---------------------- Requests ----------------------
    const getAllRequests = async (microId) => {
        try {
            setLoading(true);
            let payload = { ...selectedFilters };
            if (microId) payload.microId = microId;

            const response = await ThinkificServiceSDK.getMicrocredentials(payload);
            const data = response?.data || [];

            setState({ data, dataSource: data });
            setSelectedRows([]);
            setSelectAll(false);

            //  Detecta claves que contengan "unidad_"
            const dynamicColumns = Object.keys(data[0] || {})
                .filter(key => key.includes("unidad_"))
                .map(key => {
                    const cleanLabel = key.substring(key.indexOf("unidad_")).replace("unidad_", "Unidad ");
                    return {
                        name: key,
                        label: cleanLabel,
                        align: "center",
                        style: { minWidth: 160, textAlign: "center" }
                    };
                });

            const baseWithoutStatus = baseRequests.filter(col => col.name !== 'pok_status');
            const statusColumn = baseRequests.find(col => col.name === 'pok_status');
            const checkboxColumn = { name: "checkbox", label: "Seleccionar", align: "center" };

            setColumns([...baseWithoutStatus, ...dynamicColumns, statusColumn, checkboxColumn]);
            setLoading(false);
        } catch (err) {
            setLoading(false);
            setToast({
                toastProps: {
                    severity: "error",
                    open: true,
                    message: err?.message || "Ops! Ha ocurrido un error"
                }
            });
        }
    };

    const handleRowCheckboxChange = (rowId) => {
        setSelectedRows(prev => {
            const set = new Set(prev);
            if (set.has(rowId)) set.delete(rowId);
            else set.add(rowId);
            const newArray = Array.from(set);
            setSelectAll(state.data.length > 0 && newArray.length === state.data.length);
            return newArray;
        });
    };

    const handleSelectAllRows = () => {
        const newSelectAll = !selectAll;
        setSelectAll(newSelectAll);
        if (newSelectAll) {
            const allIds = state.data.map((r, idx) => getRowId(r, idx));
            setSelectedRows(allIds);
        } else {
            setSelectedRows([]);
        }
    };

    const handleChangeFilter = async (e) => {
        const { target: { value, name } } = e;
        const newFilters = { ...selectedFilters, [name]: value };
        setSelectedFilters(newFilters);
        if (name === 'microId') {
            await getAllRequests(value);
        }
    };

    const emitirCertificados = async () => {
        const { data } = state;
        const selectedData = data.filter((row, idx) => selectedRows.includes(getRowId(row, idx)));

        if (selectedData.length === 0) {
            setToast({
                toastProps: {
                    severity: "warning",
                    open: true,
                    message: "No hay registros seleccionados"
                }
            });
            return;
        }


        if (selectedRows.length > 2 && (!refreshInterval || refreshInterval === '')) {
            setToast({
                toastProps: {
                    severity: "warning",
                    open: true,
                    message: "Por favor selecciona primero el tiempo de recarga de la página"
                }
            });
            return;
        }

        setEmitting(true);
        setProgress(0);

        try {
            const payloads = selectedData.map(student => {
                const courses = Object.keys(student)
                    .filter(key => key.includes("_unidad_") && student[key] === "completed")
                    .map(key => {
                        const courseId = key.split("_unidad_")[0];
                        return parseInt(courseId, 10);
                    })
                    .filter(id => !isNaN(id));

                return {
                    resource: "microcredential",
                    user_id: student.user_id,
                    first_name: student.first_name,
                    last_name: student.last_name,
                    email: student.email,
                    microcredencial_Id: parseInt(selectedFilters.microId, 10),
                    courses
                };
            });

            console.log(payloads);

            const batchSize = 5;
            for (let i = 0; i < payloads.length; i += batchSize) {
                const batch = payloads.slice(i, i + batchSize);
               
                await Promise.allSettled(
                    batch.map(p => ThinkificServiceSDK.postDetectMessage(p))
                );
                setProgress(Math.min(i + batch.length, payloads.length));
                await new Promise(res => setTimeout(res, 50));
            }
            
            setToast({
                toastProps: {
                    severity: "success",
                    open: true,
                    message: "Estamos generando los certificados.Esto puede tardar un momento"
                }
            });
        } catch (error) {
            console.error("Error emitiendo certificados:", error);
            setToast({
                toastProps: {
                    severity: "error",
                    open: true,
                    message: "Hubo un error al emitir certificados"
                }
            });
        } finally {
            
            const spinnerDuration = selectedRows.length > 1 ? 15000 : 45000;
            await new Promise(resolve => setTimeout(resolve, spinnerDuration));
            setEmitting(false);
          
            if (selectedRows.length > 2) {
                setIsRefreshEnabled(true);
                updateRefreshParamsInUrl(refreshInterval, true);
            }
            // Recargar los datos después del spinner
            await getAllRequests(selectedFilters.microId);
           
        }
    };

    const columnsWithHeaderCheckbox = columns.map(col =>
        col.name === "checkbox"
            ? {
                ...col,
                label: (
                    <Checkbox
                        checked={selectAll}
                        onChange={handleSelectAllRows}
                        disabled={emitting}
                        color="primary"
                        size="small"
                        inputProps={{ 'aria-label': 'select all rows' }}
                    />
                )
            }
            : col
    );

    return (
        <Box>
            <Typography variant="h1" className={classes.title}>
                Gestión de microcredenciales
            </Typography>

            <Box className={classes.container}>
                <Card className={classes.cardHeader}>
                    <CardContent>
                        <Grid container spacing={1}>
                            <Grid item xs={6} md={3}>
                                <Box className="offset-margin-top-22">
                                    <Select
                                        items={catalogues.microId}
                                        margin="dense"
                                        variant="outlined"
                                        label="Microcredenciales"
                                        name="microId"
                                        onChange={handleChangeFilter}
                                        disabled={!catalogues.microId.length || emitting}
                                        loading={loadingMicroId}
                                        value={selectedFilters.microId || ''}
                                        size="medium"
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Box className="offset-margin-top-38" display="flex" alignItems="center">
                                    {selectedRows.length > 0 && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            style={{ marginLeft: '20%', width: '65%' }}
                                            size="small"
                                            onClick={emitirCertificados}
                                            disabled={emitting || selectedRows.length === 0}
                                        >
                                            <Typography variant='body2'>
                                                {emitting ? 'Emitiendo...' : 'Emitir Certificados'}
                                            </Typography>
                                        </Button>
                                    )}
                                </Box>
                            </Grid>
                            {(selectedRows.length > 2 || isRefreshEnabled || refreshInterval) && (
                                <Grid item xs={12} md={6}>
                                    <Box className="offset-margin-top-38" display="flex" alignItems="center">
                                        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" style={{ marginLeft: '20%', width: '65%' }}>
                                            {(selectedRows.length > 2 || refreshInterval) && (
                                                <>
                                                    <Typography 
                                                        variant="body2" 
                                                        style={{ 
                                                            fontWeight: 600,
                                                            fontSize: '14px',
                                                            color: '#9F6FFB',
                                                            marginRight: '12px'
                                                        }}
                                                    >
                                                        Recargar estatus
                                                    </Typography>
                                                    <Select
                                                        items={[
                                                            { id: 5, label: '5 segundos', value: '5' },
                                                            { id: 10, label: '10 segundos', value: '10' },
                                                            { id: 15, label: '15 segundos', value: '15' },
                                                            { id: 30, label: '30 segundos', value: '30' },
                                                            { id: 60, label: '60 segundos', value: '60' }
                                                        ]}
                                                        margin="dense"
                                                        variant="outlined"
                                                        name="refreshInterval"
                                                        onChange={(e) => {
                                                            const { target: { value } } = e;
                                                            setRefreshInterval(value);

                                                            updateRefreshParamsInUrl(value, isRefreshEnabled);
                                                        }}
                                                        value={refreshInterval || ''}
                                                        size="small"
                                                        className={selectClasses.selectRoot}
                                                        style={{ 
                                                            flex: 1
                                                        }}
                                                    />
                                                </>
                                            )}
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Switch
                                                    checked={isRefreshEnabled}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setIsRefreshEnabled(checked);
                                                        if (!checked) {
                                                           
                                                            if (refreshIntervalRef.current) {
                                                                clearInterval(refreshIntervalRef.current);
                                                                refreshIntervalRef.current = null;
                                                            }
                                                            
                                                            setRefreshInterval('');
                                                            updateRefreshParamsInUrl('', false);
                                                        } else {
                                                            
                                                            if (refreshInterval) {
                                                                updateRefreshParamsInUrl(refreshInterval, checked);
                                                            }
                                                        }
                                                       
                                                        if (refreshInterval) {
                                                            updateRefreshParamsInUrl(refreshInterval, checked);
                                                        }
                                                    }}
                                                    disabled={!refreshInterval && !isRefreshEnabled}
                                                    color="primary"
                                                    size="small"
                                                />
                                                <Typography 
                                                    variant="body2" 
                                                    style={{ 
                                                        fontWeight: 600,
                                                        color: isRefreshEnabled ? '#9F6FFB' : '#78787D',
                                                        fontSize: '14px',
                                                        minWidth: '30px'
                                                    }}
                                                >
                                                    {isRefreshEnabled ? 'On' : 'Off'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </CardContent>
                </Card>

                {/* ---------------- Tabla con LoaderContent ---------------- */}
                <Card className={classes.catalogsTable}>
                    <LoaderContent loading={loading} minHeight={500}>
                        {state.data.length > 0 ? (
                            <Table
                                maxHeight={'800px'}
                                columns={columnsWithHeaderCheckbox}
                                rows={state.data.map((row, idx) => {
                                    const rowId = getRowId(row, idx);

                                  
                                    const unidades = Object.keys(row).filter(k => k.includes("unidad_"));

                                    const todasCompletadas = unidades.every(k => (row[k] || '').toLowerCase() === "completed");
                                    const algunaPendiente = unidades.some(k => {
                                        const val = (row[k] || '').toLowerCase();
                                        return !val || val === "pending" || val === "null" || val === "in_progress";
                                    });

                                    let status;
                                    if (row.pok_status === "ERROR") {
                                        status = (
                                            <span style={{
                                                cursor: 'text',
                                                backgroundColor: '#f83b5a',
                                                border: '1px solid #faaaad',
                                                borderRadius: 0,
                                                marginLeft: 0,
                                                padding: '4px 16px',
                                                fontSize: 14,
                                                fontWeight: 600,
                                                lineHeight: '18px',
                                                display: 'block'
                                            }}>
                                                Reenviar mail
                                            </span>
                                        );
                                    } else if (todasCompletadas && (row.pok_status === null || row.pok_status === "")) {
                                        status = "Emitir Certificado";
                                    } else if (algunaPendiente && (row.pok_status === null || row.pok_status === "")) {
                                        status = "En Progreso";
                                    } else {
                                        status = row.pok_status || "En Progreso";
                                    }

                                    return {
                                        ...row,
                                        ...Object.fromEntries(
                                            unidades.map(k => [
                                                k,
                                                row[k] && row[k].toLowerCase() === "completed" ? (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '6px',
                                                    }}>
                                                        <Icon className="ri-checkbox-circle-fill" style={{ color: green[500], fontSize: 24 }} />
                                                        <Typography variant="body2" sx={{ color: '#ffffff', fontSize: '0.9rem', lineHeight: 1 }}>
                                                            Completada
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '6px',
                                                    }}>
                                                        <Icon className="ri-time-fill" style={{ color: '#b0b0b0', fontSize: 24 }} />
                                                        <Typography variant="body2" sx={{ color: '#ffffff', fontSize: '0.9rem', lineHeight: 1 }}>
                                                            Pendiente
                                                        </Typography>
                                                    </Box>
                                                )
                                            ])
                                        ),
                                        pok_status: status,
                                        checkbox: (
                                            <Checkbox
                                                checked={selectedRows.includes(rowId)}
                                                onChange={() => handleRowCheckboxChange(rowId)}
                                                disabled={emitting}
                                                color="primary"
                                                size="small"
                                                inputProps={{ 'aria-label': `select row ${rowId}` }}
                                            />
                                        ),
                                    };
                                })}
                            />
                        ) : (
                            !loading && selectedFilters.microId && (
                                <NothingFound
                                    width='200px'
                                    caption={messageTable.caption}
                                    subcaption={messageTable.subtitle}
                                />
                            )
                        )}
                    </LoaderContent>
                </Card>
            </Box>

            <Modal open={emitting} className={classes.modal}>
                <Card className={classes.card} style={{ textAlign: "center", padding: "20px" }}>
                    <CardContent>
                        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                            <div className="spinner" style={{ marginBottom: "15px" }}>
                                <Icon className="ri-loader-4-line" style={{ fontSize: 40, animation: "spin 5s linear infinite" }} />
                            </div>
                            <Typography variant="h6">Emitiendo certificados...</Typography>
                            <Typography variant="body2" color="textSecondary">
                                {`Procesados ${progress} de ${selectedRows.length}`}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Modal>

            <Toast
                {...toast.toastProps}
                duration={5000}
                onFinished={() =>
                    setToast({ toastProps: { ...toast.toastProps, open: false } })
                }
            />
        </Box>
    );
};

const mapStateToProps = (state) => ({ ...state.userReducer });
export default connect(mapStateToProps, {})(MgmtMicrocredentialsTablePage);
